import React from 'react';
import PropTypes, { any } from 'prop-types';

import PopOverMenu from '../../components/PopOverMenu';
import Utr from '../../components/text/Utr';
import Img from '../../components/Img';
import { canPlayerBeAddedToDraw } from '../../utils/draws';
import { isPlayerCheckedIntoDraw } from '../../utils/events';
import { sportTypeIds } from '../../utils/eventConstants';

import styles from './drawEdit.scss';

const DrawPlayerItemV2 = (props) => {
    const {
        player = {},
        isSelected,
        isPlaced,
        hasResult,
        hidePopOverMenu,
        isWaitlist,
        placementString,
        isNotInDraw,
        showHighSchool,
        utrType,
        isReadOnly,
        showError,
        hasBackdraw,
        isBackdraw,
        togglePlayerSelect,
        handlePlayerPopoverClick = () => { },
        organizerRatings,
        sportTypeId,
        drawId,
        displaySeedingOrder,
        handleMemberSeedOrderChanged,
        seedingManualOrder,
        setSeedingManualOrderMember,
        'data-testid': dataTestId,
    } = props;

    const canAddPlayerToDraw = canPlayerBeAddedToDraw(player);
    const title = !canAddPlayerToDraw && 'Player is not registered in any division';
    const isCheckedIn = isPlayerCheckedIntoDraw(player, drawId);
    const onPlayerClick = () => {
        if (!isBackdraw && canAddPlayerToDraw) {
            togglePlayerSelect(player.memberId);
        }
    };

    const seedOrder = seedingManualOrder.find((item) => item.memberId === player.memberId)?.seedNumber;

    return (
        <div
            className={`item d-flex align-items-center ${isPlaced ? 'placed' : ''} ${isSelected ? 'selected' : ''} ${!canAddPlayerToDraw && styles.disabled}`}
            title={title}
            onClick={onPlayerClick}
            data-testid={dataTestId}
        >
            {!isReadOnly && (
                <div className="checkbox-wrapper" data-testid={dataTestId && `${dataTestId}.checkbox`}>
                    {!isBackdraw && <div className={`checkbox-check`}><Img src="/img/icons/checkmark-icon.svg" /></div>}
                </div>
            )}
            <div className="d-flex aic">
                {displaySeedingOrder && (
                    <input
                        type="number"
                        className="seeding-order d-flex aic jcc"
                        data-testid={dataTestId && `${dataTestId}.seed-input`}
                        onChange={(e) => {
                            e.stopPropagation();
                            const { value } = e.target;
                            if (value === '') {
                            setSeedingManualOrderMember((prevOrder) => prevOrder.map((item) => (item.memberId === player.memberId
                                    ? { ...item, seedNumber: '' }
                                    : item)));
                            } else {
                                setSeedingManualOrderMember((prevOrder) => prevOrder.map((item) => (item.memberId === player.memberId
                                    ? { ...item, seedNumber: value }
                                    : item)));
                            }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => {
                            e.stopPropagation();
                            handleMemberSeedOrderChanged(player, e.target.value);
                        }}
                        value={seedOrder}
                    />
                )}
                <div className="player-details">
                    <div className={`name ${showError ? 'text-error' : 'text-dark'}`} data-testid={dataTestId && `${dataTestId}.name`}>
                        {player.firstName} {player.lastName}
                        <small><Utr sportTypeId={sportTypeId} player={player} pbrClass={styles.pbr} force={{ ratingType: 'singles', utrType, showDecimals: true, doNotMask: false }} organizerRatings={organizerRatings} isOrganizerView={true} /></small>
                        {isCheckedIn && <span className="utr-icon icon-check-mark-green pl24 checkin-mark" title="Player checked in" />}
                    </div>
                    {showError ? (
                        <div className="location text-error" data-testid={dataTestId && `${dataTestId}.location`}>{showError}</div>
                    ) : (
                        <div className="location" data-testid={dataTestId && `${dataTestId}.location`}>{showHighSchool && player.highSchool ? player.highSchool : player.location && player.location.display} {isWaitlist && <span>{placementString ? (player.location.display ? '- ' : '') + placementString : ''}</span>}</div>
                    )}
                </div>
            </div>
            {(!hidePopOverMenu) && (
                <PopOverMenu
                    popOverMenuClass="flex-right"
                    horizontalEllipsis="true"
                    data-testid={dataTestId && `${dataTestId}.menu`}
                    menuItems={[
                        (!hasResult && !isNotInDraw && !isReadOnly && !isBackdraw && (isWaitlist
                            ? { value: 'not_placed', label: 'Not Placed', memberId: player.memberId }
                            : { value: 'waitlist', label: 'Waitlist', memberId: player.memberId })),
                        (hasBackdraw && !isNotInDraw && !isReadOnly && { value: 'backdraw', label: 'Add to Back Draw', memberId: player.memberId }),
                        (isNotInDraw && !isReadOnly && { value: 'addToDraw', label: 'Add to Draw', memberId: player.memberId }),
                        { value: 'profile', label: 'View Profile', playerId: player.playerId },
                        (!isReadOnly && !isNotInDraw && { value: 'tag', label: 'Tag Player', memberId: player.memberId }),
                    ]}
                    onSelected={handlePlayerPopoverClick}
                />
            )}
        </div>
    );
};

DrawPlayerItemV2.propTypes = {
    player: PropTypes.objectOf(any).isRequired,
    isSelected: PropTypes.bool,
    isPlaced: PropTypes.bool,
    hasResult: PropTypes.bool,
    hidePopOverMenu: PropTypes.bool,
    isWaitlist: PropTypes.bool,
    placementString: PropTypes.string,
    isNotInDraw: PropTypes.bool,
    showHighSchool: PropTypes.bool,
    utrType: PropTypes.string.isRequired,
    isReadOnly: PropTypes.bool,
    showError: PropTypes.string,
    hasBackdraw: PropTypes.bool,
    isBackdraw: PropTypes.bool,
    togglePlayerSelect: PropTypes.func,
    handlePlayerPopoverClick: PropTypes.func,
    organizerRatings: PropTypes.arrayOf(any),
    sportTypeId: PropTypes.number,
    drawId: PropTypes.string,
    displaySeedingOrder: PropTypes.bool,
    handleMemberSeedOrderChanged: PropTypes.func,
    seedingManualOrder: PropTypes.arrayOf(any),
    setSeedingManualOrderMember: PropTypes.func,
    'data-testid': PropTypes.string,
};

DrawPlayerItemV2.defaultProps = {
    isSelected: false,
    isPlaced: false,
    hasResult: false,
    hidePopOverMenu: false,
    isWaitlist: false,
    placementString: '',
    isNotInDraw: false,
    showHighSchool: false,
    isReadOnly: false,
    showError: false,
    hasBackdraw: false,
    isBackdraw: false,
    togglePlayerSelect: () => {},
    handlePlayerPopoverClick: () => {},
    organizerRatings: [],
    sportTypeId: sportTypeIds.TENNIS,
    drawId: '',
    displaySeedingOrder: false,
    handleMemberSeedOrderChanged: () => { },
    seedingManualOrder: [],
    setSeedingManualOrderMember: () => { },
    'data-testid': undefined,
};

export default React.memo(DrawPlayerItemV2);
